import React, { useState, useEffect } from 'react';
import { X, Plus, Calendar, Package, AlertTriangle, User } from 'lucide-react';
import { supabase } from '@/config/supabase';
import { format } from 'date-fns';

interface NewOrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const NewOrderModal: React.FC<NewOrderModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    client_name: '',
    products: [{ name: '', quantity: 1, notes: '' }],
    deadline: format(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd'),
    scheduled_date: format(new Date(), 'yyyy-MM-dd'),
    priority: 0,
    notes: '',
    assigned_to: ''
  });
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState<any[]>([]);

  useEffect(() => {
    if (isOpen) {
      fetchClients();
    }
  }, [isOpen]);

  const fetchClients = async () => {
    try {
      const { data, error } = await supabase
        .from('clients')
        .select('id, name')
        .order('name');

      if (!error && data) {
        setClients(data);
      }
    } catch (error) {
      console.error('Błąd pobierania klientów:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.client_name.trim()) {
      alert('Podaj nazwę klienta');
      return;
    }

    if (formData.products.length === 0 || !formData.products[0].name) {
      alert('Dodaj przynajmniej jeden produkt');
      return;
    }

    setLoading(true);

    try {
      // Pobierz dane użytkownika
      const { data: userData } = await supabase.auth.getUser();

      // Generuj numer zlecenia
      const orderNumber = `${new Date().getFullYear()}/${Math.floor(Math.random() * 10000).toString().padStart(4, '0')}`;

      // Utwórz zlecenie
      const { error } = await supabase
        .from('production_orders')
        .insert({
          order_number: orderNumber,
          client_name: formData.client_name,
          products: formData.products.filter(p => p.name),
          deadline: formData.deadline,
          scheduled_date: formData.scheduled_date,
          priority: formData.priority,
          notes: formData.notes,
          status: 'planned',
          // NIE używamy company_id - brak w bazie
          created_by: userData?.user?.id || null,
          assigned_to: formData.assigned_to || null // może być null
        });

      if (error) throw error;

      alert('Zlecenie zostało dodane');
      onSuccess();
      onClose();
      
      // Reset formularza
      setFormData({
        client_name: '',
        products: [{ name: '', quantity: 1, notes: '' }],
        deadline: format(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd'),
        scheduled_date: format(new Date(), 'yyyy-MM-dd'),
        priority: 0,
        notes: '',
        assigned_to: ''
      });
    } catch (error) {
      console.error('Błąd dodawania zlecenia:', error);
      alert('Wystąpił błąd podczas dodawania zlecenia');
    } finally {
      setLoading(false);
    }
  };

  const addProduct = () => {
    setFormData(prev => ({
      ...prev,
      products: [...prev.products, { name: '', quantity: 1, notes: '' }]
    }));
  };

  const removeProduct = (index: number) => {
    setFormData(prev => ({
      ...prev,
      products: prev.products.filter((_, i) => i !== index)
    }));
  };

  const updateProduct = (index: number, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      products: prev.products.map((p, i) => 
        i === index ? { ...p, [field]: value } : p
      )
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-zinc-800 rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-white">Nowe zlecenie produkcyjne</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-all"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Klient */}
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">
              <User className="w-4 h-4 inline mr-1" />
              Klient
            </label>
            <input
              type="text"
              list="clients"
              value={formData.client_name}
              onChange={(e) => setFormData(prev => ({ ...prev, client_name: e.target.value }))}
              className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white focus:border-orange-500 focus:outline-none"
              placeholder="Wybierz lub wpisz nazwę klienta"
              required
            />
            <datalist id="clients">
              {clients.map(client => (
                <option key={client.id} value={client.name} />
              ))}
            </datalist>
          </div>

          {/* Produkty */}
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">
              <Package className="w-4 h-4 inline mr-1" />
              Produkty
            </label>
            <div className="space-y-3">
              {formData.products.map((product, index) => (
                <div key={index} className="flex gap-2">
                  <input
                    type="text"
                    value={product.name}
                    onChange={(e) => updateProduct(index, 'name', e.target.value)}
                    className="flex-1 px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white focus:border-orange-500 focus:outline-none"
                    placeholder="Nazwa produktu"
                    required={index === 0}
                  />
                  <input
                    type="number"
                    value={product.quantity}
                    onChange={(e) => updateProduct(index, 'quantity', parseInt(e.target.value))}
                    className="w-20 px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white focus:border-orange-500 focus:outline-none"
                    placeholder="Ilość"
                    min="1"
                  />
                  {formData.products.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeProduct(index)}
                      className="px-3 py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-all"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={addProduct}
                className="px-3 py-2 bg-zinc-700 text-gray-400 rounded-lg hover:bg-zinc-600 transition-all flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Dodaj produkt
              </button>
            </div>
          </div>

          {/* Daty */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">
                <Calendar className="w-4 h-4 inline mr-1" />
                Data planowana
              </label>
              <input
                type="date"
                value={formData.scheduled_date}
                onChange={(e) => setFormData(prev => ({ ...prev, scheduled_date: e.target.value }))}
                className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white focus:border-orange-500 focus:outline-none"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-400 mb-2">
                <Calendar className="w-4 h-4 inline mr-1" />
                Termin realizacji
              </label>
              <input
                type="date"
                value={formData.deadline}
                onChange={(e) => setFormData(prev => ({ ...prev, deadline: e.target.value }))}
                className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white focus:border-orange-500 focus:outline-none"
                required
              />
            </div>
          </div>

          {/* Priorytet */}
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">
              <AlertTriangle className="w-4 h-4 inline mr-1" />
              Priorytet
            </label>
            <select
              value={formData.priority}
              onChange={(e) => setFormData(prev => ({ ...prev, priority: parseInt(e.target.value) }))}
              className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white focus:border-orange-500 focus:outline-none"
            >
              <option value="0">Normalny</option>
              <option value="1">Pilne</option>
              <option value="2">Bardzo pilne</option>
            </select>
          </div>

          {/* Notatki */}
          <div>
            <label className="block text-sm font-medium text-gray-400 mb-2">
              Notatki
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              className="w-full px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white focus:border-orange-500 focus:outline-none"
              rows={3}
              placeholder="Dodatkowe informacje..."
            />
          </div>

          {/* Przyciski */}
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-zinc-700 text-gray-400 rounded-lg hover:bg-zinc-600 transition-all"
            >
              Anuluj
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:bg-gray-600 disabled:cursor-not-allowed transition-all flex items-center gap-2"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Dodawanie...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4" />
                  Dodaj zlecenie
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};